"use client";

/**
 * V2PasswordGate
 * Server-side password gate for V2 landing pages.
 * Calls a server action to verify the password and set an HTTP-only cookie.
 * On success, refreshes the page so the server component re-renders with the cookie present.
 */

import type React from "react";
import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { Lock, Mail } from "lucide-react";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchCallout } from "@/fetch-ui/FetchCallout";
import { verifyLandingPagePassword } from "../../_logic/actions/page-actions";

interface V2PasswordGateProps {
  pageId: number;
  /** When true the form also asks for an email, which must be on the event's invite list. */
  requiresInvitedEmail?: boolean;
}

/** Client component that gates access behind a password form */
export function V2PasswordGate({ pageId, requiresInvitedEmail = false }: V2PasswordGateProps): React.ReactElement {
  // Hooks
  const router = useRouter();

  // States
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handlers
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      setError(null);

      const result = await verifyLandingPagePassword(pageId, password, requiresInvitedEmail ? email : undefined);
      if (result.success) {
        router.refresh();
      } else {
        setError(result.error || "Incorrect password");
        setIsLoading(false);
      }
    },
    [pageId, password, email, requiresInvitedEmail, router]
  );

  // Render
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
          <Lock className="h-5 w-5 text-indigo-500" />
        </div>

        <div className="flex flex-col items-center gap-1 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Password Required</h1>
          <p className="text-sm font-medium text-gray-500">
            {requiresInvitedEmail
              ? "Enter your invited email and the password to view this page."
              : "Enter the password to view this page."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3">
          {requiresInvitedEmail && (
            <FetchInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              leftIcon={<Mail className="h-4 w-4" />}
              error={!!error}
              disabled={isLoading}
              autoFocus
              autoComplete="email"
              aria-label="Email"
            />
          )}
          <FetchInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            leftIcon={<Lock className="h-4 w-4" />}
            error={!!error}
            disabled={isLoading}
            autoFocus={!requiresInvitedEmail}
            aria-label="Password"
          />

          {error && <FetchCallout variant="error">{error}</FetchCallout>}

          <FetchButton
            type="submit"
            variant="primary"
            loading={isLoading}
            disabled={!password || (requiresInvitedEmail && !email.trim())}
            className="w-full touch-manipulation"
          >
            Continue
          </FetchButton>
        </form>
      </div>
    </div>
  );
}
